/**
 * PART N, the screen's half (William, 17 Oct 2026 1:05 AM: a STOP "is stopped (put on all the blocks) while under
 * review"; 1:02 AM: "We wil make sure each agent confirms the STOP and they will do this each am").
 * While a stop waits for approval the case and its row say "Stop pending approval", and Final Review lists the
 * stops oldest first with how long each has waited. The server decides (api/src/lib/stop-approval.ts); the row
 * carries its `stopPending` and `stopSince`. NO em dashes.
 */
import type { EventRow, PendingPress } from '@lib/care-model';

export const STOP_PENDING_WORDS = 'Stop pending approval';
export const STOP_PENDING_HELP = 'A stop on this case waits for approval. Its texting block is already on; the member\'s file changes when it is approved. Do not text this member meanwhile.';
export const STOPS_FILTER_LABEL = (n: number): string => `Stops ${n}`;

/** "waiting 14h", "waiting 25m", "waiting 2d" from an ISO time; '' when unknown. */
export function waitingAge(since: string | undefined | null, now: number = Date.now()): string {
  const t = since ? Date.parse(since) : NaN;
  if (!Number.isFinite(t)) return '';
  const mins = Math.max(0, Math.floor((now - t) / 60000));
  if (mins < 60) return `waiting ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `waiting ${hours}h`;
  return `waiting ${Math.floor(hours / 24)}d`;
}

/** The case as the drawer holds it: a stop waits while a provisional block is open or an Opt Out / Stop press is queued. */
export function stopPendingOfRecord(ev: {
  status?: string;
  provisionalBlocks?: ReadonlyArray<{ final?: unknown; lifted?: unknown }> | null | undefined;
  pending?: ReadonlyArray<Pick<PendingPress, 'kind' | 'op'>> | null | undefined;
}): boolean {
  // QA-1655 F9 (William, 17 Oct 2026): an open block counts in any status (the server's stopPending, mirrored).
  if ((ev.provisionalBlocks ?? []).some((b) => !b.final && !b.lifted)) return true;
  if (ev.status === 'cleared' || ev.status === 'closed-no-action' || ev.status === 'spam' || ev.status === 'closed') return false;
  // A queued Opt Out / Stop press, or a Find Member match whose decision is a stop (17 Oct 2026; the server's
  // isPendingStop in api/src/lib/stop-approval.ts, mirrored).
  return (ev.pending ?? []).some((p) => (p.kind === 'reply' && (p.op === 'opt-out' || p.op === 'stop'))
    || (p.kind === 'match' && ['opt-out-campaign', 'opt-out-all', 'stop'].includes(String((p as { body?: Record<string, unknown> }).body?.['decision'] ?? ''))));
}

/**
 * T-1025-CARE-08 (the CARE-07 review R4): a lift Care tried that did not happen leaves the block OPEN (the platform
 * still holds it), and the case says why, once per such block, in Care's fixed words (the server's liftFailed.outcome,
 * already Care's words: api/src/lib/care-decisions.ts liftOutcomeForBrowser). Only a block still open is named.
 */
/**
 * The office's ruling (26 Oct 2026 7:55 AM, R3 KEEP THE HOLD): the line also says that print, mail and kits for this
 * member are on hold until the lift retry succeeds, and how the retry is reached.
 * Second review S3: the hold sentence only when the block really holds them: it carries a careHold with a member whose
 * provisional-stop line is on file (stopLine 'written', or absent on holds written before QA-1653 F1); a campaign-scoped
 * hold says so. Without one, the plain warning.
 * Second review S4 and S1: the retry words say only a step staff can take where the case stands:
 *   cleared, in_review    Approve on the case (Final Review) retries it
 *   open statuses         send it to the Approval Queue first, then Approve there retries it
 *   closed-no-action, spam reopen it, send it to the Approval Queue, then Approve there retries it
 *   filed in the Archive, or 'closed'   nothing on this screen retries it; the office must act
 */
export const LIFT_FAILED_HOLD = 'Print, mail and kits for this member are on hold until the lift retry succeeds.';
export const LIFT_FAILED_HOLD_CAMPAIGN = 'Print, mail and kits for this member in this campaign are on hold until the lift retry succeeds.';
export const LIFT_FAILED_RETRY = 'Pressing Approve on this case retries it.';
export const LIFT_FAILED_RETRY_OPEN = 'Send the case to the Approval Queue first; pressing Approve on it there retries the lift.';
export const LIFT_FAILED_RETRY_REOPEN = 'Reopen the case and send it to the Approval Queue; pressing Approve on it there retries the lift.';
export const LIFT_FAILED_RETRY_NONE = 'Nothing on this screen retries the lift for this entry; the stop stays on until the office lifts it.';
export type LiftHoldScope = 'all' | 'campaign' | null;
export function liftFailedWords(outcome: string, attempts?: number, at: { status?: string; archived?: boolean; hold?: LiftHoldScope } = {}): string {
  const tries = typeof attempts === 'number' && attempts > 1 ? ` (tried ${attempts} times)` : '';
  const s = at.status ?? '';
  const retry = at.archived || s === 'closed' ? LIFT_FAILED_RETRY_NONE
    : s === 'cleared' || s === 'in_review' ? LIFT_FAILED_RETRY
      : s === 'closed-no-action' || s === 'spam' ? LIFT_FAILED_RETRY_REOPEN
        : LIFT_FAILED_RETRY_OPEN;
  const hold = at.hold === 'campaign' ? ` ${LIFT_FAILED_HOLD_CAMPAIGN}` : at.hold === 'all' ? ` ${LIFT_FAILED_HOLD}` : '';
  return `Care tried to lift a stop block on this case and it did not happen${tries}: ${outcome}. The block stays on, the same as on the platform, so do not contact this member.${hold} ${retry}`;
}

/** S3: the hold a block really carries on file, or null. */
export function holdOnFile(careHold: unknown): LiftHoldScope {
  if (!careHold || typeof careHold !== 'object') return null;
  const h = careHold as { memberId?: unknown; stopLine?: unknown; scope?: unknown };
  if (typeof h.memberId !== 'string' || !h.memberId.trim()) return null;
  if (h.stopLine !== undefined && h.stopLine !== 'written') return null;
  return h.scope === 'campaign' ? 'campaign' : 'all';
}

export function liftFailedLines(ev: {
  status?: string;
  archived?: boolean;
  provisionalBlocks?: ReadonlyArray<{ id?: string; final?: unknown; lifted?: unknown; careHold?: unknown; liftFailed?: { outcome?: unknown; attempts?: unknown } }> | null | undefined;
}): Array<{ id: string; words: string }> {
  const out: Array<{ id: string; words: string }> = [];
  for (const b of ev.provisionalBlocks ?? []) {
    if (b.final || b.lifted || !b.liftFailed || typeof b.liftFailed.outcome !== 'string') continue;
    out.push({
      id: String(b.id ?? out.length),
      words: liftFailedWords(b.liftFailed.outcome, typeof b.liftFailed.attempts === 'number' ? b.liftFailed.attempts : undefined,
        { ...(ev.status !== undefined ? { status: ev.status } : {}), archived: !!ev.archived, hold: holdOnFile(b.careHold) }),
    });
  }
  return out;
}

/**
 * CARE-08 review R1: a CLEARED case still holding a block whose lift failed offers Approve (retry the lift). The server
 * retries only those lifts and changes nothing else (api/src/care/event-review.ts, lib/stop-finalize.ts
 * retryFailedLifts); its answer's `message` is Care's fixed words.
 */
export const LIFT_RETRY_WORDING = {
  head: 'Stop block not lifted',
  failed: 'A stop block on this approved case was not lifted, so it stays on. Press Approve (retry the lift) to try again; nothing else on the case changes.',
  retry: 'Approve (retry the lift)',
  retrying: 'Trying...',
} as const;
export function liftRetryOffered(ev: Parameters<typeof liftFailedLines>[0] & { status?: string } | null | undefined): boolean {
  return !!ev && ev.status === 'cleared' && !ev.archived && liftFailedLines(ev).length > 0;
}
/** The words to show after the retry: the server's fixed message, else the head. */
export function liftRetryAnswer(answer: unknown): string {
  const m = (answer as { message?: unknown } | null | undefined)?.message;
  return typeof m === 'string' && m ? m : LIFT_RETRY_WORDING.head;
}

/** The Stops filter: only the rows with a stop waiting, OLDEST first. */
export function stopsOnly<T extends Pick<EventRow, 'stopPending' | 'stopSince'>>(rows: readonly T[]): T[] {
  return rows.filter((r) => r.stopPending === true).sort((a, b) => String(a.stopSince ?? '').localeCompare(String(b.stopSince ?? '')));
}
